// Pit lane operations — enable / disable / set-active-pit-marker /
// place-pit-marker / update.
//
// Pit lane has two sub-modes governed by state.activePitMarker:
//   null               → editing the pit lane spline waypoints
//   'entry' or 'exit'  → placing the pit entry/exit on the main track
// Toggle by clicking the on-canvas marker (handled in main.js); the
// sidebar offers buttons for the same toggle.

import { clearSelection } from './state.js';
import { pushHistory } from './history.js';

// 80 km/h pit speed limit. F1 default; matches shared/track-schema.
const DEFAULT_PIT_SPEED_MS = 22.2;

export function enablePitLane(state, notify) {
  if (state.trackData.pitLane) return;
  state.trackData.pitLane = {
    waypoints:  [],
    alpha:      0.5,
    width:      4.0,
    speedLimit: DEFAULT_PIT_SPEED_MS,
    entry:      null,
    exit:       null,
    pitBoxes:   [],
  };
  clearSelection(state);
  pushHistory(state, notify);
}

export function disablePitLane(state, notify) {
  if (!state.trackData.pitLane) return;
  state.trackData.pitLane = null;
  state.activePitMarker = null;
  clearSelection(state);
  pushHistory(state, notify);
}

// Toggle which pit marker is active for placement. Clicking the same
// marker twice deactivates it (so the user falls back to spline-edit
// sub-mode without leaving pit lane mode).
export function setActivePitMarker(state, name, notify) {
  state.activePitMarker = state.activePitMarker === name ? null : name;
  notify();
}

// Markers placed on the PIT-LANE SPLINE (not main trackline). The
// caller picks the right hit-test before invoking placePitMarker.
const PIT_SPLINE_MARKERS = new Set(['startFinish', 'speedZoneStart', 'speedZoneEnd']);

export function placePitMarker(state, name, segIdx, t, notify) {
  if (!state.trackData.pitLane) return;
  // 'entry' / 'exit' live in main-trackline space.
  // 'startFinish' / 'speedZoneStart' / 'speedZoneEnd' live in pit-
  // lane spline space.
  if (name !== 'entry' && name !== 'exit' && !PIT_SPLINE_MARKERS.has(name)) return;
  state.trackData.pitLane[name] = { segmentIdx: segIdx, t };
  pushHistory(state, notify);
}

// Marker placement targets pit-lane spline (vs main track).
export function isPitSplineMarker(name) {
  return PIT_SPLINE_MARKERS.has(name);
}

export function disablePitStartFinish(state, notify) {
  if (!state.trackData.pitLane?.startFinish) return;
  state.trackData.pitLane.startFinish = null;
  if (state.activePitMarker === 'startFinish') state.activePitMarker = null;
  pushHistory(state, notify);
}

export function disablePitSpeedZoneMarker(state, name, notify) {
  if (name !== 'speedZoneStart' && name !== 'speedZoneEnd') return;
  if (!state.trackData.pitLane?.[name]) return;
  state.trackData.pitLane[name] = null;
  if (state.activePitMarker === name) state.activePitMarker = null;
  pushHistory(state, notify);
}

// Patch a property on the pit lane (width / alpha / speedLimit).
// Caller picks live vs commit via the editor's applyChange pattern.
export function updatePitLane(state, patch) {
  const pl = state.trackData.pitLane;
  if (!pl) return;
  Object.assign(pl, patch);
}
